/**
 * File handling utilities
 * Common file operations for all tools
 */
class FileHandler {
    /**
     * Read file as text
     * @param {File} file - File object
     * @returns {Promise<string>} File content as text
     */
    static readAsText(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target.result);
            reader.onerror = (e) => reject(new Error('文件读取失败'));
            reader.readAsText(file, 'utf-8');
        });
    }

    /**
     * Read file as array buffer
     * @param {File} file - File object
     * @returns {Promise<ArrayBuffer>} File content as array buffer
     */
    static readAsArrayBuffer(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target.result);
            reader.onerror = (e) => reject(new Error('文件读取失败'));
            reader.readAsArrayBuffer(file);
        });
    }

    /**
     * Format file size for display
     * @param {number} bytes - File size in bytes
     * @returns {string} Formatted file size
     */
    static formatFileSize(bytes) {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    /**
     * Validate file type
     * @param {File} file - File object
     * @param {string[]} allowedTypes - Array of allowed MIME types
     * @returns {boolean} True if file type is allowed
     */
    static validateFileType(file, allowedTypes) {
        return allowedTypes.includes(file.type);
    }

    /**
     * Validate file size
     * @param {File} file - File object
     * @param {number} maxSize - Maximum size in bytes
     * @returns {boolean} True if file size is within limit
     */
    static validateFileSize(file, maxSize) {
        return file.size <= maxSize;
    }

    /**
     * Download data as file
     * @param {string} data - Data to download
     * @param {string} filename - Filename for download
     * @param {string} type - MIME type
     */
    static downloadFile(data, filename, type = 'text/plain') {
        const blob = new Blob([data], { type });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    }

    /**
     * Create file input element
     * @param {Object} options - Options for file input
     * @returns {HTMLInputElement} File input element
     */
    static createFileInput(options = {}) {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = options.accept || '*';
        input.multiple = options.multiple || false;
        return input;
    }

    /**
     * Show file size warning if needed
     * @param {File} file - File object
     * @param {HTMLElement} container - Container to show warning in
     */
    static showFileSizeWarning(file, container) {
        const existingWarning = container.querySelector('.size-warning');
        if (existingWarning) {
            existingWarning.remove();
        }

        let warningMessage = '';
        let warningClass = '';

        if (file.size > 4 * 1024 * 1024 * 1024) { // 4GB
            warningMessage = '❌ 文件过大，超出浏览器处理能力，建议使用专业工具';
            warningClass = 'size-warning-error';
        } else if (file.size > 1 * 1024 * 1024 * 1024) { // 1GB
            warningMessage = '⚠️ 大文件处理可能需要较长时间且消耗大量内存，建议只选择必要的哈希算法';
            warningClass = 'size-warning-warning';
        } else if (file.size > 100 * 1024 * 1024) { // 100MB
            warningMessage = '💡 文件较大，处理可能需要一些时间';
            warningClass = 'size-warning-info';
        }

        if (warningMessage) {
            const warningDiv = document.createElement('div');
            warningDiv.className = `size-warning ${warningClass}`;
            warningDiv.textContent = warningMessage;
            container.appendChild(warningDiv);
        }
    }
}

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
    module.exports = FileHandler;
}
